import type { RootState } from '@store/rootReducer';
import type { Action } from 'redux';

/**
 * Reading and writing the store from outside React.
 *
 * `constants/graphQLQueries.tsx` needs the signed-in user's email to stamp
 * `updatedBy` on every mutation, and `helpers/graphQLApi.tsx` reports save
 * outcomes — neither is a component, so neither can use a hook.
 *
 * This module exists rather than `import store from '@store'` because that
 * import is circular: the store builds rootSaga, whose sagas import
 * graphQLApi and graphQLQueries, which would import the store back. The cycle
 * happens to work under ESM live bindings as long as nothing touches the store
 * at module-eval time, which is exactly the kind of thing that breaks later
 * for reasons nobody can find. So the store registers itself here instead, and
 * nothing in this file imports it.
 */

interface StoreLike {
  getState: () => RootState;
  dispatch: (action: Action) => unknown;
}

let store: StoreLike | null = null;

/** Called once by store/index.tsx, immediately after the store is created. */
export const registerStore = (s: StoreLike) => {
  store = s;
};

/**
 * The current state, or null before the store is registered.
 *
 * Null is a real case in tests, which import query builders without booting
 * the app. Callers must handle it — hence `currentUserDetails` below rather
 * than a bare getState at every call site.
 */
export const getStoreState = (): RootState | null => store?.getState() ?? null;

export const dispatchToStore = (action: Action) => store?.dispatch(action);

export interface CurrentUserDetails {
  firstName: string;
  lastName: string;
  email: string;
}

const NO_USER: CurrentUserDetails = { firstName: '', lastName: '', email: '' };

/**
 * The signed-in user, read straight from the login profile slice.
 *
 * This used to be a separate Zustand store that the Sidebar container copied
 * the Redux profile into on every render. One source of truth now, and no
 * window where the copy is a render behind the original.
 */
export const currentUserDetails = (): CurrentUserDetails => {
  const details = getStoreState()?.loginUserProfile?.profile?.personalDetails;
  if (!details) return NO_USER;
  return {
    firstName: details.firstName ?? '',
    lastName: details.lastName ?? '',
    email: details.email ?? '',
  };
};
